import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { teamService } from '../../services/team';
import type { TeamMember } from '../../types/team';
import toast from 'react-hot-toast';

interface BulkManagerModalProps {
  campaignIds: number[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkManagerModal: React.FC<BulkManagerModalProps> = ({
  campaignIds,
  open,
  onClose,
  onSuccess,
}) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [selectedManagerId, setSelectedManagerId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      loadMembers();
      setSelectedManagerId('');
    }
  }, [open]);

  const loadMembers = async () => {
    setIsLoading(true);
    try {
      const data = await teamService.getMembers();
      setMembers(data);
    } catch (error) {
      console.error('Failed to load team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedManagerId) {
      toast.error('Please select a manager or choose to unassign');
      return;
    }

    setIsSubmitting(true);
    try {
      // Import the service here to avoid circular dependency
      const { bulkAssignManager } = await import('../../services/campaigns');

      const managerId = selectedManagerId === 'unassign' ? null : parseInt(selectedManagerId);
      await bulkAssignManager(campaignIds, managerId);

      const action = managerId ? 'assigned' : 'unassigned';
      toast.success(`Manager ${action} for ${campaignIds.length} campaigns`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to assign manager:', error);
      const errorMessage = error.response?.data?.message || 'Failed to assign manager';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Assign Manager to Campaigns">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-gray-50 p-3 rounded-md">
          <p className="text-sm text-gray-600">
            Assigning manager to {campaignIds.length} campaign{campaignIds.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Manager
          </label>
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading team members...</p>
          ) : (
            <select
              value={selectedManagerId}
              onChange={(e) => setSelectedManagerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={isSubmitting}
              required
            >
              <option value="">-- Select a manager --</option>
              <option value="unassign">Unassign (No manager)</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name} ({member.email}) - {member.role}
                </option>
              ))}
            </select>
          )}
          <p className="mt-1 text-xs text-gray-500">
            This will replace any existing manager assignments on the selected campaigns
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !selectedManagerId}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Assigning...' : 'Assign Manager'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

BulkManagerModal.displayName = 'BulkManagerModal';
